import { toast } from 'sonner';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Calendar, Pencil, Trash2 } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import paymentsService from '../services/paymentsService';
import { exportRowsToCsv } from '../utils/csvExport';
import { formatDate, formatDateTime } from '../utils/datetime';
import ListPager, { usePagedRows } from '../components/ListPager';
import { useTableSort, SortTh } from '../components/SortableTable';
import HelpLink from '../components/HelpLink';

/**
 * Payments — the money LOG. Read, filter, export; edit/delete only for
 * off-system rows (check/cash typos).
 *
 * REFUNDS DO NOT LIVE HERE (owner decision 2026-08-03). Both refund
 * doors are on the customer profile's Billing History:
 *   - "Refund" next to Add credit — returns credit-balance money
 *     (overpayment, downgrade, move-out), the mirror of Add credit.
 *   - the per-line refund on a paid invoice row — returns exactly what
 *     was paid on THAT invoice and voids the charge in one step (the
 *     NSF-fee case).
 * Customer names below link straight to that profile.
 */
const Payments = () => {
  const { facilityId, currentFacility, loading: facilityLoading } = useFacility();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // Explicit date RANGE — combines with (and overrides) the preset pills
  // when set. Presets stay for one-click speed; the range answers
  // "show me March" without leaving the page.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Filter pill for the payment_method column. 'all' shows every method;
  // the other options are built dynamically from the distinct
  // payment_method values present in the loaded payments.
  const [methodFilter, setMethodFilter] = useState('all');
  // Free-text search — same affordance Invoices has. Without it, finding
  // one tenant's payment meant paging or exporting to CSV.
  const [search, setSearch] = useState('');

  const fetchPayments = useCallback(async () => {
    if (!facilityId) {
      if (!facilityLoading) setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await paymentsService.getAll(facilityId);
      setPayments(res.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  }, [facilityId, facilityLoading]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete this $${p.amount.toFixed(2)} payment? The rental balance will be restored.`)) return;
    try {
      await paymentsService.remove(facilityId, p._id);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete payment.');
    }
  };

  // Distinct payment_method values present in the loaded payments, sorted
  // for stable pill ordering. Drives the method filter pills below.
  const methods = [...new Set(payments.map(p => p.payment_method).filter(Boolean))].sort();

  const filterPayments = () => {
    const now = new Date();
    const q = search.trim().toLowerCase();
    return payments.filter(p => {
      if (methodFilter !== 'all' && p.payment_method !== methodFilter) return false;
      if (q) {
        // Customer, method, notes and the processor reference — the four
        // things an operator has in hand when hunting a payment.
        const hay = `${p.customer_name || ''} ${p.payment_method || ''} `
          + `${p.notes || ''} ${p.processor_ref || p.stripe_payment_id || ''}`;
        if (!hay.toLowerCase().includes(q)) return false;
      }
      const iso = String(p.payment_date || '').slice(0, 10);
      if (fromDate && iso < fromDate) return false;
      if (toDate && iso > toDate) return false;
      if (fromDate || toDate) return true;   // explicit range wins over pills
      if (filter === 'all') return true;
      const d = new Date(p.payment_date);
      if (filter === 'today') return d.toDateString() === now.toDateString();
      if (filter === 'week') return d >= new Date(now.getTime() - 7 * 86400000);
      if (filter === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      return true;
    });
  };

  const filtered = filterPayments();
  // Net of refunds (matches the bank; per-row badges carry the detail).
  const total = filtered
    .reduce((s, p) => s + p.amount - (p.refunded_amount || 0), 0)
    .toFixed(2);

  // Every column sorts. Amount sorts NET of refunds — the figure the row
  // actually shows — so the sort can't disagree with the eye.
  const SORT_ACCESSORS = React.useMemo(() => ({
    payment_date: (p) => p.payment_date,
    customer: (p) => p.customer_name,
    amount: (p) => Number(p.amount || 0) - Number(p.refunded_amount || 0),
    method: (p) => p.payment_method,
    notes: (p) => p.notes,
    created_at: (p) => p.created_at,
    updated_at: (p) => p.updated_at,
  }), []);
  // Default: newest CREATED first (owner, 2026-08-05). payment_date is a
  // bare date, so a check recorded today for last week buried itself on
  // page 3; created_at is when the row actually appeared.
  const { applySort, sortKey, sortDir, toggle } = useTableSort(
    SORT_ACCESSORS, { key: 'created_at', dir: 'desc' });
  const pager = usePagedRows(applySort(filtered));

  const exportCsv = () => exportRowsToCsv(
    `payments-${new Date().toISOString().slice(0, 10)}`,
    filtered,
    [
      { key: 'payment_date', label: 'Date' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'amount', label: 'Amount' },
      { key: 'payment_method', label: 'Method' },
      { key: 'processor_ref', label: 'Reference',
        value: (r) => r.processor_ref || r.stripe_payment_id || '' },
      { key: 'notes', label: 'Notes' },
    ],
  );

  if (loading || facilityLoading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!facilityId) return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
      <p className="text-[13px] text-slate-500 mt-1">Create a facility in Settings to record payments.</p>
    </div>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Payments <HelpLink slug="payments" /></h1>
          <p className="text-[13px] text-slate-400 mt-0.5">{filtered.length} transactions</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          {/* Take Payment -- the live Stripe Elements card-entry flow.
              Operator types the card; works the same whether the tenant
              is on the phone, walking up at the counter, or sitting next
              to the operator. Distinct from "Record Payment" below
              which is for after-the-fact ledger entries (check, cash,
              off-system swipe). Refunds live on the customer profile. */}
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700 transition-colors"
          >
            Export CSV
          </button>
          <Link
            to="/payments/new"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700 transition-colors"
            title="Record a payment that was collected outside mySMS360 (check, cash, external terminal)"
          >
            Record Payment
          </Link>
          <Link
            to="/payments/take"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Take Payment
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-xl border border-slate-200/80 bg-white p-4">
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Total</p>
          <p className="text-xl font-semibold text-emerald-600 mt-1">${total}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">{filtered.length} transactions</p>
        </div>
        <div className="md:col-span-3 rounded-xl border border-slate-200/80 bg-white p-4">
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-2.5">Filter</p>
          <div className="flex flex-wrap items-center gap-1.5">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border border-slate-200 rounded-md px-2 py-1 text-[12px] text-slate-600"
              title="From date"
            />
            <span className="text-[12px] text-slate-400">–</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border border-slate-200 rounded-md px-2 py-1 text-[12px] text-slate-600"
              title="To date"
            />
            {(fromDate || toDate) && (
              <button
                onClick={() => { setFromDate(''); setToDate(''); }}
                className="text-[12px] text-slate-400 hover:text-slate-600"
              >
                Clear
              </button>
            )}
            {['all', 'today', 'week', 'month'].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${
                  filter === f
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
              >{f.charAt(0).toUpperCase() + f.slice(1)}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Payment-method filter pills, built from the distinct payment_method
          values in the loaded list (plus an "All" pill). Filters client-side
          and combines with the date filter above. */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-4">
        {methods.length > 0 && [{ key: 'all', label: 'All' }, ...methods.map((m) => ({ key: m, label: m }))].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setMethodFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
              methodFilter === f.key
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customer, method, note, or reference..."
          className="w-64 max-w-full px-3 py-1.5 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
        />
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
          <thead>
            <tr className="bg-slate-50/50 border-b border-slate-100">
              {[
                { k: 'payment_date', label: 'Date' },
                { k: 'customer', label: 'Customer' },
                { k: 'amount', label: 'Amount' },
                { k: 'method', label: 'Method' },
                { k: 'notes', label: 'Notes' },
                { k: 'created_at', label: 'Created' },
                { k: 'updated_at', label: 'Updated' },
              ].map(({ k, label }) => (
                <SortTh key={k} label={label} sortKey={k}
                  current={sortKey} dir={sortDir} onSort={toggle} />
              ))}
              <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filtered.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-[13px] text-slate-400">No payments found</td></tr>
            ) : (
              pager.pageRows.map((p) => {
                const isStripe = Boolean(p.stripe_payment_id);
                return (
                <tr key={p._id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 text-[13px] text-slate-500">
                    <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3 text-slate-300" />{formatDate(p.payment_date, currentFacility?.timezone)}</span>
                  </td>
                  <td className="px-4 py-3 text-[13px] font-medium text-slate-700">
                    {/* Refunds live on the profile — the name is the door. */}
                    {p.customer_id ? (
                      <Link
                        to={`/customers/${p.customer_id}/edit`}
                        className="hover:text-blue-600 hover:underline"
                        title="Open the customer — refunds live on their Billing History"
                      >
                        {p.customer_name || 'Unknown'}
                      </Link>
                    ) : (p.customer_name || 'Unknown')}
                  </td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-emerald-600">
                    ${p.amount.toFixed(2)}
                    {(p.refunded_amount || 0) >= p.amount - 0.005 ? (
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-200">Refunded</span>
                    ) : (p.refunded_amount || 0) > 0 ? (
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                        &minus;${p.refunded_amount.toFixed(2)} refunded
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 text-blue-600">{p.payment_method}</span>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-slate-400">{p.notes || '—'}</td>
                  {/* Stored naive-UTC; shown on the FACILITY clock. */}
                  <td className="px-4 py-3 text-[12px] text-slate-400 whitespace-nowrap">
                    {p.created_at ? formatDateTime(p.created_at, currentFacility?.timezone) : '—'}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-slate-400 whitespace-nowrap">
                    {p.updated_at ? formatDateTime(p.updated_at, currentFacility?.timezone) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {/* Edit/Delete stay off Stripe rows: the money moved
                          through the processor, so the ledger row is a record
                          of what Stripe did, not a figure to retype. Undoing
                          it is a refund — on the customer profile. */}
                      {!isStripe && (
                        <>
                          <Link to={`/payments/${p._id}/edit`} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Edit">
                            <Pencil className="h-3.5 w-3.5" />
                          </Link>
                          <button onClick={() => handleDelete(p)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
        <ListPager {...pager} noun="payments" />
      </div>
    </div>
  );
};

export default Payments;
